import { useTranslation } from "react-i18next";
import { PlusCircleIcon, TrashIcon } from "@heroicons/react/outline";
import { SignatureConfig } from "../interfaces";
import SmallTextInputField from "./SmallTextInputField";
import { useMemo } from "react";

interface Props {
  signatureConfig: SignatureConfig;
  updateSignatureConfig: (updates: Partial<SignatureConfig>) => void;
  handleSaveAndNext: () => void;
  handleBack: () => void;
}

function SignaturesSidebarRecipients(props: Props) {
  const { t } = useTranslation();
  const { signatureConfig } = props;

  function handleUpdateNumRecipients(delta: number) {
    const currentAmount = (signatureConfig.numRecipients ?? 1);
    const newAmount = Math.max(0, Math.min(currentAmount + delta, 4));

    props.updateSignatureConfig({ "numRecipients": newAmount });
  }

  const isValid = useMemo(() => {
    const num = signatureConfig.numRecipients;

    if (num >= 1)
      if (!signatureConfig.recipientEmail1.includes("@") || !signatureConfig.recipientName1)
        return false;

    if (num >= 2)
      if (!signatureConfig.recipientEmail2.includes("@") || !signatureConfig.recipientName2)
        return false;

    if (num >= 3)
      if (!signatureConfig.recipientEmail3.includes("@") || !signatureConfig.recipientName3)
        return false;

    if (num >= 4)
      if (!signatureConfig.recipientEmail4.includes("@") || !signatureConfig.recipientName4)
        return false;

    return true;

  }, [signatureConfig])

  return (

    <div className="flex flex-col w-[360px] h-full bg-white flex-shrink-0 pt-6 px-4 border-l">
      <div className="text-2xl font-semibold font-gilroy mb-2">
        Request Signatures
      </div>
      <div className="text-sm font-semibold text-gray-400 mb-2">
        {t("Enter your recipient(s) email then configure where they are to sign on the next page.")}
      </div>


      {/* <div className="flex items-center text-xs gap-4 mb-4">
        <button className="btn btn-white w-full px-0 py-2 outline">Email Recipients</button>
        <button className="btn btn-white w-full px-0 py-2" disabled>Public Link</button>
      </div> */}

      <div className="h-px w-full bg-gray-300 flex-shrink-0" />

      {!!signatureConfig &&
        <div className="flex flex-col gap-4 overflow-y-scroll scrollbar-hidden py-4">
          {signatureConfig.numRecipients >= 1 &&
            <div className="flex flex-col border border-blue rounded-xl overflow-hidden flex-shrink-0">
              <div className="text-blue border-blue border-b bg-blue/10  px-4 py-2 flex items-center justify-between">
                <div className="text-sm font-semibold">
                  1st Recipient
                </div>
                {signatureConfig.numRecipients === 0 &&
                  <TrashIcon className="w-4 h-4 cursor-pointer" onClick={() => handleUpdateNumRecipients(-1)} />
                }
              </div>
              <div className="flex flex-col gap-4 p-2">
                <SmallTextInputField label="Name" value={signatureConfig.recipientName1} onChange={(value) => props.updateSignatureConfig({ "recipientName1": value })} />
                <SmallTextInputField label="Email" value={signatureConfig.recipientEmail1} onChange={(value) => props.updateSignatureConfig({ "recipientEmail1": value })} />
              </div>
            </div>
          }

          {signatureConfig.numRecipients >= 2 &&
            <div className="flex flex-col border border-purple rounded-xl overflow-hidden flex-shrink-0">
              <div className="text-purple border-purple border-b bg-purple/10  px-4 py-2 flex items-center justify-between">
                <div className="text-sm font-semibold">
                  2nd Recipient
                </div>
                {signatureConfig.numRecipients === 2 &&
                  <TrashIcon className="w-4 h-4 cursor-pointer" onClick={() => handleUpdateNumRecipients(-1)} />
                }
              </div>
              <div className="flex flex-col gap-4 p-2">
                <SmallTextInputField label="Name" value={signatureConfig.recipientName2} onChange={(value) => props.updateSignatureConfig({ "recipientName2": value })} />
                <SmallTextInputField label="Email" value={signatureConfig.recipientEmail2} onChange={(value) => props.updateSignatureConfig({ "recipientEmail2": value })} />
              </div>
            </div>
          }

          {signatureConfig.numRecipients >= 3 &&
            <div className="flex flex-col border border-green rounded-xl overflow-hidden flex-shrink-0">
              <div className="text-green border-green border-b bg-green/10  px-4 py-2 flex items-center justify-between">
                <div className="text-sm font-semibold">
                  3rd Recipient
                </div>
                {signatureConfig.numRecipients === 3 &&
                  <TrashIcon className="w-4 h-4 cursor-pointer" onClick={() => handleUpdateNumRecipients(-1)} />
                }
              </div>
              <div className="flex flex-col gap-4 p-2">
                <SmallTextInputField label="Name" value={signatureConfig.recipientName3} onChange={(value) => props.updateSignatureConfig({ "recipientName3": value })} />
                <SmallTextInputField label="Email" value={signatureConfig.recipientEmail3} onChange={(value) => props.updateSignatureConfig({ "recipientEmail3": value })} />
              </div>
            </div>
          }

          {signatureConfig.numRecipients >= 4 &&
            <div className="flex flex-col border border-red rounded-xl overflow-hidden flex-shrink-0">
              <div className="text-red border-red border-b bg-red/10  px-4 py-2 flex items-center justify-between">
                <div className="text-sm font-semibold">
                  4th Recipient
                </div>
                {signatureConfig.numRecipients === 4 &&
                  <TrashIcon className="w-4 h-4 cursor-pointer" onClick={() => handleUpdateNumRecipients(-1)} />
                }
              </div>
              <div className="flex flex-col gap-4 p-2">
                <SmallTextInputField label="Name" value={signatureConfig.recipientName4} onChange={(value) => props.updateSignatureConfig({ "recipientName4": value })} />
                <SmallTextInputField label="Email" value={signatureConfig.recipientEmail4} onChange={(value) => props.updateSignatureConfig({ "recipientEmail4": value })} />
              </div>
            </div>
          }

          {signatureConfig.numRecipients < 4 &&
            <button className="btn btn-gray border rounded font-semibold text-xs flex justify-center items-center gap-2 px-4 py-1 w-fit" onClick={() => handleUpdateNumRecipients(1)}>
              <PlusCircleIcon className="w-4 h-4" />
              {t("Add recipient")}
            </button>
          }
        </div>
      }

      <div className="h-px w-full bg-gray-300 flex-shrink-0 mt-auto" />
      <div className="flex items-center text-sm gap-2 mt-4 mb-4">
        <button className="btn btn-gray border w-full px-0 py-2" onClick={props.handleBack}>Close</button>
        <button className="btn btn-blue w-full px-0 py-2" onClick={props.handleSaveAndNext} disabled={!isValid}>Next</button>
      </div>
    </div>
  );

}

export default SignaturesSidebarRecipients;
